import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCrosshairs,
  FaArrowLeft,
  FaChevronRight,
  FaPaperPlane,
  FaRegEnvelope,
} from 'react-icons/fa';
import api from '../../../utils/api';
import safeStorage from '../../../utils/safeStorage';
import { PASSWORD_HELP_TEXT, describePasswordProblems } from '../../../utils/passwordPolicy';
import { YButton } from '../../../ui/YButton';
import { YFormField } from '../../../ui/YFormField';
import { YInput } from '../../../ui/YInput';
import { YCheckbox } from '../../../ui/YCheckbox';
import { AuthShell } from './AuthShell';
import { AuthStepper } from './AuthStepper';
import { AuthPasswordInput } from './AuthPasswordInput';
import { AuthInputWithIcon } from './AuthInputWithIcon';
import {
  GOOGLE_POPUP_REDIRECT_URI,
  GoogleGIcon,
  requestGoogleAuthCode,
} from '../googleIdentityButton';
import { captureTagOutCodeFromSearch, postAuthPath } from '../../billing/tagOutCode';
import '../styles/auth-design.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SIGNUP_STEPS = ['Account', 'Hunter', 'Field Kit'];

const HUNTING_METHODS = [
  { value: 'rifle', label: 'Rifle' },
  { value: 'bow', label: 'Bow' },
  { value: 'muzzleloader', label: 'Muzzleloader' },
  { value: 'shotgun', label: 'Shotgun' },
  { value: 'crossbow', label: 'Crossbow' },
  { value: 'other', label: 'Other' },
];

function AuthOrDivider() {
  return (
    <div className="auth-or-divider" aria-hidden="true">
      <span className="auth-or-divider__rule" />
      <span className="auth-mono-label">OR</span>
      <span className="auth-or-divider__rule" />
    </div>
  );
}

function AuthSwitchLine({ prompt, action, onClick }) {
  return (
    <p className="auth-switch-line">
      {prompt}{' '}
      <button type="button" onClick={onClick}>
        {action}
      </button>
    </p>
  );
}

function AuthPanelHeading({ title, subtitle }) {
  return (
    <div className="auth-panel__heading">
      {title && <h1 className="auth-panel__title">{title}</h1>}
      {subtitle && <p className="auth-panel__subtitle">{subtitle}</p>}
    </div>
  );
}

export function AuthForm({ onLogin, onSignup, onResendVerification, loading = false }) {
  const location = useLocation();
  const [isSignup, setIsSignup] = useState(false);
  const [signupStep, setSignupStep] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [verificationPending, setVerificationPending] = useState(false);

  useEffect(() => {
    captureTagOutCodeFromSearch(location.search);
  }, [location.search]);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});

  const [signupData, setSignupData] = useState({
    username: '',
    email: '',
    password1: '',
    password2: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    preferred_hunting_method: 'rifle',
  });
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [signupErrors, setSignupErrors] = useState({});
  const [googleBusy, setGoogleBusy] = useState(false);

  const resetTransientState = () => {
    setError('');
    setSuccess('');
    setLoginErrors({});
    setSignupErrors({});
    setSignupStep(0);
    setTermsAgreed(false);
    setVerificationPending(false);
    setVerificationEmail('');
  };

  const switchToLogin = () => {
    setIsSignup(false);
    resetTransientState();
    setLoginIdentifier('');
    setLoginPassword('');
    setSignupData({
      username: '',
      email: '',
      password1: '',
      password2: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      preferred_hunting_method: 'rifle',
    });
  };

  const switchToSignup = () => {
    setIsSignup(true);
    resetTransientState();
    setLoginIdentifier('');
    setLoginPassword('');
  };

  const handleSignupInputChange = (field, value) => {
    setSignupData((prev) => ({ ...prev, [field]: value }));
  };

  const validateLogin = () => {
    const next = {};
    if (!loginIdentifier.trim()) {
      next.id = 'Enter your username, email, or phone';
    }
    if (!loginPassword) {
      next.pw = 'Enter your password';
    }
    setLoginErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateSignupStep = () => {
    const next = {};
    if (signupStep === 0) {
      if (!EMAIL_RE.test(signupData.email)) {
        next.email = 'Enter a valid email address';
      }
      const passwordProblem = describePasswordProblems(signupData.password1);
      if (passwordProblem) {
        next.password1 = passwordProblem;
      }
      if (signupData.password2 !== signupData.password1) {
        next.password2 = "Passwords don't match";
      }
    } else if (signupStep === 1) {
      if (!signupData.first_name.trim()) next.first_name = 'Required';
      if (!signupData.last_name.trim()) next.last_name = 'Required';
      if (signupData.username.trim().length < 3) {
        next.username = 'At least 3 characters';
      }
    } else if (signupStep === 2) {
      if (!termsAgreed) next.agree = 'Please accept to continue';
    }
    setSignupErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validateLogin()) return;

    try {
      await onLogin(loginIdentifier, loginPassword);
    } catch (err) {
      if (err.response?.data?.code === 'email_not_verified') {
        setVerificationPending(true);
        setVerificationEmail(err.response.data.email || loginIdentifier);
        return;
      }
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.non_field_errors) {
        setError(err.response.data.non_field_errors[0]);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
      }
    }
  };

  const handleSignupSubmit = async () => {
    setError('');
    try {
      const result = await onSignup(signupData);
      if (result?.verificationRequired) {
        setVerificationPending(true);
        setVerificationEmail(result.email || signupData.email);
        setSignupStep(3);
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.username) {
        setError(err.response.data.username[0]);
      } else if (err.response?.data?.password1) {
        setError(err.response.data.password1[0]);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Signup failed. Please try again.');
      }
    }
  };

  const handleSignupContinue = async () => {
    setError('');
    if (!validateSignupStep()) return;
    if (signupStep < 2) {
      setSignupStep((s) => s + 1);
      return;
    }
    await handleSignupSubmit();
  };

  const handleSignupBack = () => {
    setError('');
    setSignupErrors({});
    if (signupStep === 0) {
      switchToLogin();
    } else {
      setSignupStep((s) => s - 1);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail || resendLoading) return;
    setResendLoading(true);
    setError('');
    setSuccess('');
    try {
      if (onResendVerification) {
        await onResendVerification(verificationEmail);
      } else {
        await api.post('/auth/resend-verification/', { email: verificationEmail });
      }
      setSuccess('Verification email sent! Please check your inbox.');
    } catch {
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    switchToLogin();
  };

  const finishGoogleSession = (data) => {
    try {
      safeStorage.setItem('token', data.access);
      safeStorage.setItem('refresh_token', data.refresh);
    } catch {
      /* ignore storage errors */
    }
    window.location.href = postAuthPath({ fallback: '/' });
  };

  const handleGoogleClick = async () => {
    setError('');
    setGoogleBusy(true);
    try {
      const code = await requestGoogleAuthCode();
      if (!code) {
        return;
      }
      const resp = await api.post('/auth/google/verify/', {
        code,
        redirect_uri: GOOGLE_POPUP_REDIRECT_URI,
      });
      const data = resp.data;
      if (!resp || !data) {
        setError('Google sign-in failed');
        return;
      }
      finishGoogleSession(data);
    } catch (e) {
      const body = e?.response?.data || {};
      if (body?.code === 'account_exists') {
        setError('Account exists for this email. Please log in and link Google.');
      } else if (e?.message === 'Google script not loaded') {
        setError('Google script not loaded');
      } else {
        setError(body.error || e.message || 'Google sign-in failed');
      }
    } finally {
      setGoogleBusy(false);
    }
  };

  const showVerify = verificationPending;

  return (
    <AuthShell>
      <div className="auth-panel">
        {showVerify ? (
          <>
            <div className="auth-verify">
              <div className="auth-verify__icon">
                <FaRegEnvelope size={28} />
              </div>
              <h2 className="auth-verify__title">Check your email</h2>
              <p className="auth-verify__text">We sent a verification link to</p>
              <p className="auth-verify__email">{verificationEmail || 'your inbox'}</p>
              <div className="auth-verify__tag-wrap">
                <span className="auth-status-tag">Link expires in 24h</span>
              </div>
            </div>
            {error && (
              <Alert variant="danger" className="auth-alert">
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" className="auth-alert">
                {success}
              </Alert>
            )}
            <div className="auth-form-stack">
              <YButton
                variant="secondary"
                block
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? (
                  'Sending…'
                ) : (
                  <>
                    <FaPaperPlane />
                    Resend verification email
                  </>
                )}
              </YButton>
              <YButton variant="ghost" block onClick={handleBackToLogin}>
                Back to sign in
              </YButton>
            </div>
          </>
        ) : !isSignup ? (
          <>
            <AuthPanelHeading title="Welcome back" subtitle="Hunt, Harvest, Holler" />
            {error && (
              <Alert variant="danger" className="auth-alert">
                {error}
              </Alert>
            )}
            <YButton
              type="button"
              variant="ghost"
              block
              className="auth-google-btn"
              onClick={handleGoogleClick}
              disabled={loading || googleBusy}
            >
              <GoogleGIcon />
              {googleBusy ? 'Connecting…' : 'Continue with Google'}
            </YButton>
            <AuthOrDivider />
            <form className="auth-form-stack" onSubmit={handleLogin} noValidate>
              <YFormField
                label="Username, email, or phone"
                htmlFor="login-id"
                error={loginErrors.id}
              >
                <AuthInputWithIcon
                  id="login-id"
                  icon={FaUser}
                  value={loginIdentifier}
                  onChange={(e) => setLoginIdentifier(e.target.value)}
                  placeholder="j.wilson / you@email.com"
                  disabled={loading}
                  invalid={!!loginErrors.id}
                />
              </YFormField>
              <YFormField label="Password" htmlFor="login-pw" error={loginErrors.pw}>
                <AuthPasswordInput
                  id="login-pw"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  invalid={!!loginErrors.pw}
                />
              </YFormField>
              <div className="auth-row-between">
                <YCheckbox
                  id="keep-signed-in"
                  label="Keep me signed in"
                  checked={keepSignedIn}
                  onChange={setKeepSignedIn}
                  disabled={loading}
                />
                <Link to="/forgot-password" className="auth-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <YButton type="submit" variant="primary" block disabled={loading}>
                {loading ? (
                  'Signing in…'
                ) : (
                  <>
                    <FaCrosshairs />
                    Sign In
                  </>
                )}
              </YButton>
            </form>
            <AuthSwitchLine
              prompt="New to Yona?"
              action="Create an account"
              onClick={switchToSignup}
            />
          </>
        ) : (
          <>
            <AuthPanelHeading
              title="Create your account"
              subtitle="For the Southern Outdoorsman"
            />
            <AuthStepper step={Math.min(signupStep, SIGNUP_STEPS.length - 1)} />
            {error && (
              <Alert variant="danger" className="auth-alert">
                {error}
              </Alert>
            )}

            {signupStep === 0 && (
              <div className="auth-form-stack">
                <YButton
                  type="button"
                  variant="ghost"
                  block
                  className="auth-google-btn"
                  onClick={handleGoogleClick}
                  disabled={loading || googleBusy}
                >
                  <GoogleGIcon />
                  {googleBusy ? 'Connecting…' : 'Sign up with Google'}
                </YButton>
                <AuthOrDivider />
                <YFormField label="Email address" htmlFor="signup-email" error={signupErrors.email}>
                  <AuthInputWithIcon
                    id="signup-email"
                    type="email"
                    icon={FaEnvelope}
                    value={signupData.email}
                    onChange={(e) => handleSignupInputChange('email', e.target.value)}
                    placeholder="you@email.com"
                    disabled={loading}
                    invalid={!!signupErrors.email}
                  />
                </YFormField>
                <YFormField
                  label="Password"
                  htmlFor="signup-pw1"
                  error={signupErrors.password1}
                  help={!signupErrors.password1 ? PASSWORD_HELP_TEXT : undefined}
                >
                  <AuthPasswordInput
                    id="signup-pw1"
                    value={signupData.password1}
                    onChange={(e) => handleSignupInputChange('password1', e.target.value)}
                    placeholder="Create a password"
                    disabled={loading}
                    invalid={!!signupErrors.password1}
                  />
                </YFormField>
                <YFormField
                  label="Confirm password"
                  htmlFor="signup-pw2"
                  error={signupErrors.password2}
                >
                  <AuthPasswordInput
                    id="signup-pw2"
                    value={signupData.password2}
                    onChange={(e) => handleSignupInputChange('password2', e.target.value)}
                    placeholder="Re-enter your password"
                    disabled={loading}
                    invalid={!!signupErrors.password2}
                  />
                </YFormField>
              </div>
            )}

            {signupStep === 1 && (
              <div className="auth-form-stack">
                <div className="auth-name-grid">
                  <YFormField label="First name" htmlFor="first-name" error={signupErrors.first_name}>
                    <YInput
                      id="first-name"
                      value={signupData.first_name}
                      onChange={(e) => handleSignupInputChange('first_name', e.target.value)}
                      placeholder="James"
                      disabled={loading}
                      invalid={!!signupErrors.first_name}
                    />
                  </YFormField>
                  <YFormField label="Last name" htmlFor="last-name" error={signupErrors.last_name}>
                    <YInput
                      id="last-name"
                      value={signupData.last_name}
                      onChange={(e) => handleSignupInputChange('last_name', e.target.value)}
                      placeholder="Wilson"
                      disabled={loading}
                      invalid={!!signupErrors.last_name}
                    />
                  </YFormField>
                </div>
                <YFormField
                  label="Username"
                  htmlFor="username"
                  error={signupErrors.username}
                  help={!signupErrors.username ? 'How clubmates will find you' : undefined}
                >
                  <AuthInputWithIcon
                    id="username"
                    icon={FaUser}
                    value={signupData.username}
                    onChange={(e) => handleSignupInputChange('username', e.target.value)}
                    placeholder="j.wilson"
                    disabled={loading}
                    invalid={!!signupErrors.username}
                  />
                </YFormField>
                <YFormField
                  label="Phone number"
                  htmlFor="phone"
                  help="Optional — you can use this to log in later"
                >
                  <AuthInputWithIcon
                    id="phone"
                    type="tel"
                    icon={FaPhone}
                    value={signupData.phone_number}
                    onChange={(e) => handleSignupInputChange('phone_number', e.target.value)}
                    placeholder="(205) 555-0147"
                    disabled={loading}
                  />
                </YFormField>
              </div>
            )}

            {signupStep === 2 && (
              <div className="auth-form-stack" style={{ gap: 18 }}>
                <div>
                  <span className="auth-mono-label">PREFERRED HUNTING METHOD</span>
                  <div className="auth-method-grid" role="radiogroup" aria-label="Preferred hunting method">
                    {HUNTING_METHODS.map((method) => {
                      const selected = signupData.preferred_hunting_method === method.value;
                      return (
                        <button
                          key={method.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`auth-method-chip ${selected ? 'is-selected' : ''}`}
                          onClick={() =>
                            handleSignupInputChange('preferred_hunting_method', method.value)
                          }
                          disabled={loading}
                        >
                          {method.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="auth-terms-box">
                  <YCheckbox
                    id="terms-agree"
                    checked={termsAgreed}
                    onChange={setTermsAgreed}
                    disabled={loading}
                    label={
                      <span>
                        I agree to the{' '}
                        <a href="/terms" onClick={(e) => e.preventDefault()}>
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="/conservation" onClick={(e) => e.preventDefault()}>
                          Conservation Pledge
                        </a>
                        .
                      </span>
                    }
                  />
                  {signupErrors.agree && (
                    <div className="y-error" role="alert" style={{ marginTop: 8 }}>
                      {signupErrors.agree}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="auth-nav-row">
              <YButton
                type="button"
                variant="ghost"
                onClick={handleSignupBack}
                disabled={loading}
              >
                <FaArrowLeft />
                {signupStep === 0 ? 'Sign in' : 'Back'}
              </YButton>
              <YButton
                type="button"
                variant="primary"
                className="y-btn-grow"
                onClick={handleSignupContinue}
                disabled={loading}
              >
                {loading
                  ? 'Creating account…'
                  : signupStep === 2
                    ? 'Create Account'
                    : 'Continue'}
                {!loading && <FaChevronRight />}
              </YButton>
            </div>

            {signupStep === 0 && (
              <AuthSwitchLine
                prompt="Already have an account?"
                action="Sign in"
                onClick={switchToLogin}
              />
            )}
          </>
        )}
      </div>
    </AuthShell>
  );
}
